/**
 * Боты под ручным управлением админа — перехват управления в сетевой игре.
 *
 * Админ, который смотрит чужой матч, может перехватить управление ботом (см.
 * TAKE_BOT_CONTROL на сервере и BotControlModal в списке игроков): у игрока-бота
 * в состоянии появляется botControlledByUserId. Пока перехвативший онлайн, за
 * этого бота ходит он сам, и ни сервер, ни клиенты-делегаты за него не играют.
 * Ушел оффлайн — пометка остается, но бот снова ходит как обычно.
 *
 * Движок бота везде решает по player.isBot: ходить ли за текущего игрока, кого
 * подтверждать на этапе ближнего боя, кто из людей завершает этап. Поэтому
 * вместо «кроме такого-то бота» по всему движку ему просто отдается состояние,
 * где такие боты помечены живыми (isBot: false). Реальное состояние при этом
 * не меняется — маска только на чтение.
 */

/**
 * Можно ли перехватить управление этим игроком. Только у «неявного» бота:
 * явный бот рейтингового матча помечен ботом для всех, и играть за него
 * админу нельзя. Проверяют и сервер (TAKE_BOT_CONTROL), и список игроков.
 */
export const checkIfBotControlTakeoverAllowed = (player) =>
  Boolean(player && player.isBot && !player.isVisibleBot);

/**
 * Боты, которыми сейчас вручную управляет онлайн-админ.
 * @param {Array} players - игроки из состояния партии
 * @param {Function} isControllerOnline - (userId) => онлайн ли перехвативший
 * @returns {string[]} id таких ботов (id из состояния партии)
 */
export const getControlledBotIds = (players, isControllerOnline) =>
  (players || [])
    .filter((player) => player.isBot && player.botControlledByUserId
      && isControllerOnline(player.botControlledByUserId))
    .map((player) => player.id);

/**
 * Состояние, в котором перечисленные боты выглядят для движка живыми игроками.
 * Возвращает исходный объект, если маскировать нечего, — чтобы не плодить
 * копии на каждом чтении.
 */
export const maskControlledBots = (state, controlledBotIds) => {
  if (!controlledBotIds || controlledBotIds.length === 0) {
    return state;
  }
  const gameState = state?.shared?.gameState;
  if (!gameState || !Array.isArray(gameState.players)) {
    return state;
  }

  const controlled = new Set(controlledBotIds);
  if (!gameState.players.some((player) => player.isBot && controlled.has(player.id))) {
    return state;
  }

  return {
    ...state,
    shared: {
      ...state.shared,
      gameState: {
        ...gameState,
        players: gameState.players.map((player) => (
          controlled.has(player.id) ? {...player, isBot: false} : player
        )),
      },
    },
  };
};

/**
 * Обертка стора для движка бота: чтение — через маску, запись — в настоящий
 * стор без изменений. Маска кэшируется по ссылке на состояние: движок читает
 * состояние много раз за шаг, и копия нужна одна на каждую его версию.
 *
 * Сервер создает обертку заново на каждый прогон авто-действий, поэтому
 * метку «снабжение этому игроку уже роздано» она держит на оборачиваемом
 * сторе (autoActionsTrackerKey, см. executePhaseAutoActions). Иначе метка
 * терялась бы вместе с оберткой, и сервер раздавал бы снабжение заново после
 * каждого действия — включая только что отмененное админом. Включение и
 * снятие перехвата посреди хода метку тоже не сбрасывают: с маской и без
 * прогоны сходятся на один ключ
 */
export const createControlledBotsMaskedStore = (store, controlledBotIds) => {
  if (!controlledBotIds || controlledBotIds.length === 0) {
    return store;
  }

  let lastRawState = null;
  let lastMaskedState = null;

  return {
    ...store,
    autoActionsTrackerKey: store.autoActionsTrackerKey || store,
    getState: () => {
      const rawState = store.getState();
      if (rawState !== lastRawState) {
        lastRawState = rawState;
        lastMaskedState = maskControlledBots(rawState, controlledBotIds);
      }
      return lastMaskedState;
    },
    dispatch: (action) => store.dispatch(action),
  };
};
